import { cssJoin } from '@volvo-cars/css/utils';
import { IconButton } from '@volvo-cars/react-icons';
import type React from 'react';
import { forwardRef, useState } from 'react';
import { TextLikeInput } from './text-input';
import type { DateLikeProps } from './types';

export interface DateInputProps extends DateLikeProps {
  /**
   * The earliest date/time to accept, in the `yyyy-mm-dd` format.
   */
  min?: string;

  /**
   * The latest date/time to accept, in the `yyyy-mm-dd` format.
   */
  max?: string;

  /**
   * What type of information to autocomplete in the input.
   */
  autoComplete?: string;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: string;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: string;

  /**
   * Fires when the input’s value is changed.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * Lets the users select a date from the native calendar dropdown.
 * Should be used when it is helpful for the user to see what weekday a selected date is.
 *
 * When entering a well known date (such as a birth date), prefer a TextInput with a date pattern.
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  function DateInput(props, ref) {
    return <DateLikeInput {...props} type="date" ref={ref} />;
  },
);

type DateLikeInputProps = DateInputProps & {
  type: string;
};

export const DateLikeInput = forwardRef<HTMLInputElement, DateLikeInputProps>(
  function DateLikeInput(
    {
      variant = 'top-label',
      value,
      onChange,
      onBlur,
      defaultValue,
      type,
      ...props
    },
    ref,
  ) {
    const [isUncontrolledBlank, setIsUncontrolledBlank] = useState(
      variant === 'floating-label' && !value && !defaultValue,
    );
    function updateIsBlank(event: React.ChangeEvent<HTMLInputElement>) {
      // Use constraints validation to determine if the input is completely blank.
      // `value` will be empty even if the input is partially filled, but `badInput` will be true.
      const { validity } = event.currentTarget;
      const initiallyRequired = event.currentTarget.required;
      if (!initiallyRequired) {
        event.currentTarget.required = true;
      }
      setIsUncontrolledBlank(validity.valueMissing && !validity.badInput);
      if (!initiallyRequired) {
        event.currentTarget.required = false;
      }
    }

    const isBlank = typeof value === 'string' ? !value : isUncontrolledBlank;
    return (
      <TextLikeInput
        {...props}
        variant={variant}
        value={value}
        defaultValue={defaultValue}
        ref={ref}
        type={type}
        onBlur={
          variant === 'floating-label'
            ? (event) => {
                onBlur?.(event);
                updateIsBlank(event);
              }
            : onBlur
        }
        onChange={
          variant === 'floating-label'
            ? (event) => {
                onChange?.(event);
                updateIsBlank(event);
              }
            : onChange
        }
        data-blank={isBlank ? '' : undefined}
        contentAfter={
          <IconButton
            icon="calendar"
            className={cssJoin('ml-0', variant === 'floating-label' && '-mr-8')}
            bleed={variant === 'top-label'}
            variant="clear"
            // No need for a separate button for screen reader users,
            // they will interact with the native form controls.
            aria-hidden
            tabIndex={-1}
            aria-label=""
            onClick={(event) => {
              const input =
                event.currentTarget.parentElement?.querySelector<HTMLInputElement>(
                  'input[type^=date]',
                );
              if (input) {
                try {
                  input.showPicker();
                } catch {
                  // Use brute force to trigger the date picker.
                  input.click();
                  input.focus();
                }
              }
            }}
          />
        }
      />
    );
  },
);
